/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  EMAProviderOrder,
  EMAProviderOrder$inboundSchema,
} from "./emaproviderorder.js";

export const IdentityChainingPreparationStage = {
  Selection: "selection",
  Eligibility: "eligibility",
  Registration: "registration",
  Discovery: "discovery",
  Publication: "publication",
} as const;
export type IdentityChainingPreparationStage = ClosedEnum<
  typeof IdentityChainingPreparationStage
>;

/**
 * Preparation state; readiness never proves user access.
 */
export const IdentityChainingPreparationState = {
  UnsupportedProfile: "unsupported_profile",
  IncompleteMetadata: "incomplete_metadata",
  UnknownGrants: "unknown_grants",
  ManualSetupRequired: "manual_setup_required",
  ConfigurationRequired: "configuration_required",
  InProgress: "in_progress",
  Indeterminate: "indeterminate",
  ProviderRejection: "provider_rejection",
  TransientFailure: "transient_failure",
  PublishedAcceptanceUnverified: "published_acceptance_unverified",
  Ready: "ready",
  Unlinked: "unlinked",
} as const;
/**
 * Preparation state; readiness never proves user access.
 */
export type IdentityChainingPreparationState = ClosedEnum<
  typeof IdentityChainingPreparationState
>;

export type IdentityChainingPreparation = {
  bindingId?: string | undefined;
  /**
   * Exact selected remote_session_client row ID, when available.
   */
  clientId?: string | undefined;
  /**
   * The EMA providers one vendor authorization server accepts, saved or default. They are stored as an ordered list that identity chaining tries in turn, but each endpoint can use only the provider its sign-in goes through, so the list acts as the set of accepted providers.
   */
  emaProviders?: EMAProviderOrder | undefined;
  /**
   * Public OAuth client identifier, never a secret.
   */
  externalClientId?: string | undefined;
  generation: number;
  /**
   * Provenance of recorded grants, not provider trust.
   */
  grantSource: string;
  /**
   * Null means unknown; an empty array means no recorded grants.
   */
  grantTypes: Array<string> | null;
  issuer?: string | undefined;
  /**
   * When the vendor last accepted an assertion for this binding from a provider that can serve it now, so chaining has worked for it. Omitted when none is recorded for a usable provider or acceptance could not be read. Returned by readEMA only.
   */
  lastAcceptedAt?: Date | undefined;
  /**
   * Safe next action without provider bodies or credentials.
   */
  remediation: string;
  resource: string;
  retryable: boolean;
  scopes: Array<string>;
  stage: IdentityChainingPreparationStage;
  /**
   * Preparation state; readiness never proves user access.
   */
  state: IdentityChainingPreparationState;
};

/** @internal */
export const IdentityChainingPreparationStage$inboundSchema: z.ZodMiniEnum<
  typeof IdentityChainingPreparationStage
> = z.enum(IdentityChainingPreparationStage);

/** @internal */
export const IdentityChainingPreparationState$inboundSchema: z.ZodMiniEnum<
  typeof IdentityChainingPreparationState
> = z.enum(IdentityChainingPreparationState);

/** @internal */
export const IdentityChainingPreparation$inboundSchema: z.ZodMiniType<
  IdentityChainingPreparation,
  unknown
> = z.pipe(
  z.object({
    binding_id: z.optional(z.string()),
    client_id: z.optional(z.string()),
    ema_providers: z.optional(EMAProviderOrder$inboundSchema),
    external_client_id: z.optional(z.string()),
    generation: z.int(),
    grant_source: z.string(),
    grant_types: z.nullable(z.array(z.string())),
    issuer: z.optional(z.string()),
    last_accepted_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    remediation: z.string(),
    resource: z.string(),
    retryable: z.boolean(),
    scopes: z.array(z.string()),
    stage: IdentityChainingPreparationStage$inboundSchema,
    state: IdentityChainingPreparationState$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "binding_id": "bindingId",
      "client_id": "clientId",
      "ema_providers": "emaProviders",
      "external_client_id": "externalClientId",
      "grant_source": "grantSource",
      "grant_types": "grantTypes",
      "last_accepted_at": "lastAcceptedAt",
    });
  }),
);

export function identityChainingPreparationFromJSON(
  jsonString: string,
): SafeParseResult<IdentityChainingPreparation, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => IdentityChainingPreparation$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'IdentityChainingPreparation' from JSON`,
  );
}
